import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import StaffUtils from 'services/StaffUtils';

function useLockDocument(documentId: string, shouldLock: boolean) {
  const intervalRef = useRef<NodeJS.Timer | null>(null);
  const lockRequestInProgress = useRef(false);
  const dispatch = useAppDispatch();
  const authentication = useAppSelector((state) => state.authentication);
  const physicians = useAppSelector((state) => state.physicians.byId);
  const admins = useAppSelector((state) => state.admins.byId);
  const lockedToId = useAppSelector((state) => state.documents.locked[documentId])!;

  const willRequest = !lockedToId && shouldLock && !lockRequestInProgress.current;

  const [loading, setLoading] = useState(willRequest);

  const ownId = authentication.user!.id;

  const lockedStaff = admins[lockedToId] || physicians[lockedToId];

  useEffect(() => {
    if (willRequest) {
      lockRequestInProgress.current = true;
      setLoading(true);

      dispatch(
        api.Documents.setLockedDocument({ id: documentId, booleanResource: { value: true } }),
      )
        .then(() => {
          setLoading(false);
          lockRequestInProgress.current = false;
        })
        .catch(() => {
          setLoading(false);
          lockRequestInProgress.current = false;
        });
    }
  }, [dispatch, documentId, willRequest]);

  useEffect(() => {
    // Clear any existing interval when component mounts or dependencies change
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (shouldLock && ownId === lockedToId) {
      intervalRef.current = setInterval(() => {
        // Only send a new lock request if we're not already processing one
        if (!lockRequestInProgress.current) {
          lockRequestInProgress.current = true;
          dispatch(
            api.Documents.setLockedDocument({ id: documentId, booleanResource: { value: true } }),
          ).finally(() => {
            lockRequestInProgress.current = false;
          });
        }
      }, 25 * 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;

        // Only unlock if we're the ones who locked it
        if (ownId === lockedToId) {
          dispatch(
            api.Documents.setLockedDocument({ id: documentId, booleanResource: { value: false } }),
          );
        }
      }
    };
  }, [dispatch, ownId, lockedToId, documentId, shouldLock]);

  const lockedName = useMemo(() => {
    if (lockedToId) {
      return StaffUtils.getName(lockedStaff);
    }
  }, [lockedToId, lockedStaff]);

  return [lockedToId, lockedName, !!loading];
}

export default useLockDocument;
